<script>
  import { createEventDispatcher, tick } from "svelte";
  import { Button } from "paxray-components-svelte";

  export let open = false;
  export let readElement = "";

  const dispatch = createEventDispatcher();

  let selectedPositions = new Set();
  let panelEl;
  let panelWidth = null;

  $: if (open) {
    panelWidth = null;
    tick().then(() => {
      if (panelEl) panelWidth = panelEl.offsetWidth;
    });
  } else {
    panelWidth = null;
  }

  $: chars = readElement ? [...readElement] : [];

  $: if (!open) {
    selectedPositions = new Set();
  }

  function togglePosition(index) {
    if (index >= chars.length - 1) return;
    const newSet = new Set(selectedPositions);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    selectedPositions = newSet;
  }

  $: previewSlices = (() => {
    if (!chars.length) return [readElement];
    const slices = [];
    let current = "";
    chars.forEach((char, i) => {
      current += char;
      if (selectedPositions.has(i)) {
        slices.push(current);
        current = "";
      }
    });
    if (current) slices.push(current);
    return slices;
  })();

  function handleConfirm() {
    dispatch("confirm", { slices: previewSlices });
    open = false;
  }

  $$restProps;
</script>

{#if open}
  <div
    class="slice-panel"
    bind:this={panelEl}
    style={panelWidth ? `width: ${panelWidth}px` : ""}
  >
    <span class="anon-type header">Slice Element</span>
    <div class="divider"></div>

    <span class="anon-type subtitle">
      Select Slice Positions (Element will be sliced after selected positions)
    </span>

    <div class="chars-wrapper">
      {#each chars as char, i}
        <div
          class="option"
          class:selected={selectedPositions.has(i)}
          class:last-char={i === chars.length - 1}
          role="button"
          tabindex={i < chars.length - 1 ? 0 : -1}
          on:mousedown={() => togglePosition(i)}
          on:keydown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              togglePosition(i);
            }
          }}
        >
          {char}
        </div>
      {/each}
    </div>

    <span class="anon-type header">Preview</span>
    <div class="divider"></div>
    <div class="preview-wrapper">
      {#each previewSlices as slice}
        <span class="option" title={slice}>{slice}</span>
      {/each}
    </div>

    <div class="divider"></div>
    <div class="action_wrapper">
      <Button
        text="Cancel"
        on:click={() => (open = false)}
        kind="secondary"
        size="field"
      />
      <Button
        text="Confirm"
        on:click={handleConfirm}
        kind="secondary"
        size="field"
      />
    </div>
  </div>
{/if}

<style>
  .slice-panel {
    position: absolute;
    top: 100%;
    left: 0;
    width: auto;
    background: #fff;
    border-radius: 0.3125rem;
    box-shadow: 0 0.125rem 0.5rem rgba(44, 44, 44, 0.15);
    z-index: 10;
    margin-top: 0.25rem;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .anon-type {
    font-size: 0.5rem;
    font-style: normal;
    font-weight: 400;
    line-height: 100%;
    letter-spacing: -0.0055rem;
    padding: 0 0.44rem;
  }

  .anon-type.subtitle {
    font-size: 0.6rem;
    white-space: nowrap;
  }

  .divider {
    height: 0;
    border-bottom: 1px solid var(--pax-black, #1c1c20);
    margin: 0 0.44rem;
  }

  .chars-wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.22rem;
    padding: 0 0.44rem;
  }

  .option {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #f2f2f2;
    padding: 0.31rem 0.44rem;
    margin: 0.11rem 0;
    border-radius: 0.3125rem;
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: -0.00825rem;
    color: #1c1c20;
    cursor: pointer;
    transition: background 0.2s;
    user-select: none;
    min-width: 1.5rem;
  }

  .option.selected {
    background: #8dcfff;
  }

  .option.last-char {
    cursor: default;
    opacity: 0.5;
  }

  .preview-wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.44rem;
    padding: 0 0.44rem;
  }

  .preview-wrapper .option {
    cursor: default;
    background: #f2f2f2;
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: inline-block;
  }

  .action_wrapper {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    justify-content: flex-end;
    padding: 0 0.44rem;
  }
</style>
